import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
    FormProvider,
    useFieldArray,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod'
import { Plus, Save, Trash2 } from 'lucide-react'

import type {
    CreateInNoteItem,
    CreateInNoteRequest,
} from '@/lib/api/product-lot-api'
import type { ProductLotOption } from '@/components/product-lots/ProductLotItemsField'
import type { LotOption } from '@/components/lots/lot-items-field'
import type { LotPickerSource } from '@/components/lots/lot-picker-source'
import { ledgerSourceNoteOption } from '@/components/lots/lot-option-builders'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { productLotToOption } from '@/components/product-lots/product-lot-option'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { ProductLotItemsField } from '@/components/product-lots/ProductLotItemsField'
import { LotSourcesSubTable } from '@/components/lots/lot-sources-subtable'
import {
    LotDebtFillSection,
    sumOtherRowsFill,
} from '@/components/product-lots/lot-debt-fill-section'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { QuantityInput } from '@/components/ui/quantity-input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useCreateProductLotInNote } from '@/hooks/use-product-lot-mutation'
import { LotPicker } from '@/components/lots/lot-picker'
import {
    usePackagingLotSource,
    useProductLotSource,
    useScentingLotSource,
} from '@/components/lots/lot-picker-source'
import {
    useScentingWithdrawalNote,
    useScentingWithdrawalNotesInfinite,
} from '@/hooks/use-scenting-lot-query'
import { formatQty } from '@/lib/format'
import {
    usePackagingLots,
    usePackagingWithdrawalNote,
    usePackagingWithdrawalNotesInfinite,
} from '@/hooks/use-packaging-lot-query'
import { useCheckPackagingAvailability } from '@/hooks/use-packaging-query'
import { useDebounced } from '@/hooks/use-debounced'
import { productLotApi } from '@/lib/api/product-lot-api'
import { normalizeLotDiscount } from '@/lib/lot-discount'
import { logEntryToFormValues } from '@/components/packaging-log/log-entry-prefill'
import { boxLogEntryToFormValues } from '@/components/box-log/log-entry-prefill'
import { otherLogEntryToFormValues } from '@/components/other-log/log-entry-prefill'
import { usePackagingLogEntry } from '@/hooks/use-packaging-log-query'
import { usePackagingBoxLogEntry } from '@/hooks/use-packaging-box-log-query'
import { usePackagingOtherLogEntry } from '@/hooks/use-packaging-other-log-query'
import { autofillMaterials } from '@/lib/note-material-autofill'
import { m } from '@/lib/paraglide/messages'

// Nguồn tầng dòng (reason=from_packaging): lô lấy hương nguồn + kg. Bắt buộc kiểm
// ở superRefine cấp form (biết được reason). Ở đây chỉ khai optional.
const sourceItemFields = {
    // Danh sách nhiều lô lấy hương nguồn (tuỳ chọn ở tầng field). Bắt buộc ≥1 khi
    // reason=from_packaging kiểm ở superRefine cấp form.
    sources: z
        .array(
            z.object({
                source_lot_id: z.string().optional(),
                source_quantity: z.number().optional(),
            }),
        )
        .optional(),
    // Bảng vật tư con (tuỳ chọn). Giữ permissive ở tầng field — bắt buộc từng dòng
    // (đã thêm thì phải chọn lô + kg > 0) kiểm ở superRefine cấp form.
    materials: z
        .array(
            z.object({
                packaging_lot_id: z.string().optional(),
                quantity: z.number().optional(),
            }),
        )
        .optional(),
    // Bù đơn đang nợ lô (FD-372), keyed theo lot_debt_id. Chỉ có ở dòng LÔ MỚI —
    // lô có sẵn không tra được sản phẩm từ option nên khối bù không hiện.
    debt_allocations: z.record(z.string(), z.number()).optional(),
}

const itemSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('new'),
        new_lot_code: z.string().optional(),
        new_lot_product_id: z
            .string({ message: m.form_err_pick_product() })
            .min(1, m.form_err_pick_product()),
        new_lot_warehouse_id: z
            .string()
            .min(1, m.form_err_pick_warehouse())
            .optional(),
        new_lot_position_id: z
            .string({ message: m.warehouse_position_required() })
            .uuid({ message: m.warehouse_position_required() }),
        new_lot_manufactured_date: z.string().optional(),
        new_lot_expiry_date: z.string().optional(),
        // Tuỳ chọn — bỏ trống nghĩa là lô không đánh dấu CK. Input dùng
        // setValueAs (xem ProductLotItemsField) nên ô rỗng vào đây là
        // undefined, không phải NaN.
        new_lot_discount_percent: z
            .number()
            .min(0, m.form_err_discount_range())
            .max(100, m.form_err_discount_range())
            .optional(),
        // #M-16: id dòng SẢN PHẨM ĐÍCH của sổ đóng gói sinh ra dòng này. Chỉ đi
        // kèm khi form được mở từ một cuốn sổ; server dùng nó để nối dòng sổ với
        // lô vừa tạo, không phải suy theo sản phẩm.
        log_output_id: z.string().optional(),
        quantity: z
            .number({ message: m.form_err_quantity_required() })
            .positive(m.form_err_quantity_positive()),
        ...sourceItemFields,
    }),
    z.object({
        kind: z.literal('existing'),
        product_lot_id: z
            .string({ message: m.form_err_pick_existing_lot() })
            .min(1, m.form_err_pick_existing_lot()),
        quantity: z
            .number({ message: m.form_err_quantity_required() })
            .positive(m.form_err_quantity_positive()),
        ...sourceItemFields,
    }),
])

// Vật tư đóng gói tuỳ chọn — nhưng đã thêm dòng thì phải chọn lô + số lượng > 0.
// Dùng chung cho mọi lý do cho phép khai vật tư (2 luồng đóng gói + dần chè).
function validateMaterials(
    ctx: z.RefinementCtx,
    materials:
        Array<{ packaging_lot_id?: string; quantity?: number }> | undefined,
    idx: number,
) {
    ;(materials ?? []).forEach((mat, mIdx) => {
        if (!mat.packaging_lot_id) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['items', idx, 'materials', mIdx, 'packaging_lot_id'],
                message: m.pll_new_err_material_lot(),
            })
        }
        if (!mat.quantity || mat.quantity <= 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['items', idx, 'materials', mIdx, 'quantity'],
                message: m.pll_new_err_material_qty(),
            })
        }
    })
}

// Enum đủ mọi lý do PNK thành phẩm — khi sửa 1 phiếu tạo tự động (from_order_cancel,
// from_customer_return) resolver vẫn phải chấp nhận reason đó. Màn tạo chỉ cho
// chọn các lý do thủ công (xem Select bên dưới); màn sửa khoá reason.
const schema = z
    .object({
        reason: z.enum([
            'from_packaging',
            'from_product_packaging',
            'adjustment',
            'from_manufacturing_partner',
            'from_tea_sifting',
            'from_agent_return',
            'from_customer_return',
            'from_order_cancel',
        ]),
        // Bắt buộc khi reason=from_packaging (xem superRefine). PXK vật tư luôn
        // tuỳ chọn — không phải SP nào cũng tốn vật tư đóng gói.
        scenting_lot_stock_out_note_id: z.string().optional(),
        packaging_lot_stock_out_note_id: z.string().optional(),
        notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
        items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
    })
    .superRefine((data, ctx) => {
        // Luồng đi chuỗi (from_packaging | from_product_packaging): mỗi dòng phải
        // khai ≥1 lô nguồn; mỗi nguồn có lô + kg xuất >= 0 (cho phép 0, cấm âm/để
        // trống); không khai trùng lô.
        const isChain =
            data.reason === 'from_packaging' ||
            data.reason === 'from_product_packaging'
        // from_tea_sifting (dần chè) KHÔNG khai lô nguồn nhưng vẫn khai vật tư.
        const allowsMaterials = isChain || data.reason === 'from_tea_sifting'
        if (!allowsMaterials) return
        data.items.forEach((it, idx) => {
            const sources = it.sources ?? []
            if (!isChain) {
                validateMaterials(ctx, it.materials, idx)
                return
            }
            if (sources.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['items', idx, 'sources'],
                    message: m.pll_new_err_source_lot(),
                })
            } else {
                const seen = new Set<string>()
                sources.forEach((s, sIdx) => {
                    if (!s.source_lot_id) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [
                                'items',
                                idx,
                                'sources',
                                sIdx,
                                'source_lot_id',
                            ],
                            message: m.pll_new_err_source_lot(),
                        })
                    } else if (seen.has(s.source_lot_id)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [
                                'items',
                                idx,
                                'sources',
                                sIdx,
                                'source_lot_id',
                            ],
                            message: m.pll_new_err_source_dup(),
                        })
                    } else {
                        seen.add(s.source_lot_id)
                    }
                    if (s.source_quantity == null || s.source_quantity < 0) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [
                                'items',
                                idx,
                                'sources',
                                sIdx,
                                'source_quantity',
                            ],
                            message: m.pll_new_err_source_qty(),
                        })
                    }
                })
            }
            validateMaterials(ctx, it.materials, idx)
        })
    })

type FormData = z.infer<typeof schema>

// Picker lô lấy hương nguồn + ô kg xuất + nút Lấy hết cho MỘT dòng (from_packaging).
// TẠM ẨN phiếu lấy hàng khỏi UI (tính năng item-level-lot-lineage chưa hoàn thiện).
// Đặt = true để bật lại 2 picker phiếu lấy hàng (lấy hương + vật tư). Không xoá code/BE.
const WITHDRAWAL_UI_ENABLED = false

/** Cuộn tải thêm cho picker lô vật tư (khi không chọn phiếu lấy hàng). */
interface MaterialLoadMore {
    onLoadMore?: () => void
    hasMore?: boolean
    loadingMore?: boolean
}

// Một dòng vật tư trong bảng con: picker lô vật tư + kg + nút "lấy hết" + xoá.
function MaterialRow({
    idx,
    mIdx,
    options,
    onSearchChange,
    clientFilter,
    onRemove,
    onLoadMore,
    hasMore,
    loadingMore,
}: {
    idx: number
    mIdx: number
    options: LotOption[]
    onSearchChange: (s: string) => void
    clientFilter?: boolean
    onRemove: () => void
} & MaterialLoadMore) {
    const { control, register, setValue, formState } =
        useFormContext<FormData>()
    const picked = useWatch({
        control,
        name: `items.${idx}.materials.${mIdx}.packaging_lot_id`,
    })
    // Lô đã chọn ở các dòng vật tư KHÁC của cùng item → loại khỏi options để
    // không chọn trùng (bảng con có UNIQUE(item, lô); trùng sẽ vỡ khi lưu).
    const siblings = useWatch({
        control,
        name: `items.${idx}.materials`,
    }) as Array<{ packaging_lot_id?: string }> | undefined
    const siblingIds = useMemo(
        () =>
            (siblings ?? [])
                .filter((_, i) => i !== mIdx)
                .map((s) => s?.packaging_lot_id ?? '')
                .filter(Boolean),
        [siblings, mIdx],
    )
    const matErr = (
        formState.errors.items?.[idx] as
            | { materials?: Array<Record<string, { message?: string }>> }
            | undefined
    )?.materials?.[mIdx]
    // Lô vật tư đã chọn được cache để nhãn + hint "còn X" không rớt khỏi trigger
    // khi search server đổi options (server chỉ trả danh sách theo từ khoá).
    const [pickedCache, setPickedCache] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const pickedOption =
        options.find((o) => o.value === picked) ??
        (picked ? pickedCache.get(picked) : undefined)
    const remaining = pickedOption?.available
    // Ô chọn lô vật tư dùng LotPicker chung; nguồn = options (server hoặc lô
    // trong phiếu lấy hàng vật tư) + lô đã chọn được cache.
    const rowSource: LotPickerSource = {
        options,
        getOption: (id) =>
            id
                ? (options.find((o) => o.value === id) ?? pickedCache.get(id))
                : undefined,
        onSearchChange,
        onLoadMore,
        hasMore,
        loadingMore,
    }

    return (
        <div className="mt-1 grid gap-2 sm:grid-cols-2">
            <div className="min-w-0">
                <LotPicker
                    source={rowSource}
                    clientFilter={!!clientFilter}
                    excludeIds={siblingIds}
                    value={picked ?? ''}
                    onChange={(v, opt) => {
                        if (opt)
                            setPickedCache((prev) => {
                                if (prev.has(v)) return prev
                                const next = new Map(prev)
                                next.set(v, opt)
                                return next
                            })
                        setValue(
                            `items.${idx}.materials.${mIdx}.packaging_lot_id`,
                            v,
                            { shouldValidate: true },
                        )
                    }}
                    placeholder={m.pll_new_material_lot_placeholder()}
                />
                {matErr?.packaging_lot_id?.message && (
                    <p className="mt-1 text-xs text-destructive">
                        {matErr.packaging_lot_id.message}
                    </p>
                )}
                {remaining != null && (
                    <p className="mt-1 text-xs text-muted-foreground">
                        {m.lot_items_available_hint({
                            qty: formatQty(remaining),
                            // Vật tư: đơn vị theo chính lô (cái, hộp…), KHÔNG mặc
                            // định 'kg'. Rỗng nếu lô không kèm đơn vị.
                            unit: pickedOption?.unit ?? '',
                        })}
                    </p>
                )}
            </div>
            <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <QuantityInput
                            unit={pickedOption?.unit || undefined}
                            type="number"
                            step="0.001"
                            min="0"
                            placeholder={m.pll_new_material_qty_label()}
                            {...register(
                                `items.${idx}.materials.${mIdx}.quantity`,
                                {
                                    setValueAs: (v) =>
                                        v === '' || v == null
                                            ? undefined
                                            : Number(v),
                                },
                            )}
                        />
                        {remaining != null && remaining > 0 && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="shrink-0"
                                onClick={() =>
                                    setValue(
                                        `items.${idx}.materials.${mIdx}.quantity`,
                                        remaining,
                                        { shouldValidate: true },
                                    )
                                }
                            >
                                {m.lot_items_take_all()}
                            </Button>
                        )}
                    </div>
                    {matErr?.quantity?.message && (
                        <p className="mt-1 text-xs text-destructive">
                            {matErr.quantity.message}
                        </p>
                    )}
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    aria-label={m.pll_new_material_remove()}
                    onClick={onRemove}
                >
                    <Trash2 className="size-4 text-destructive" />
                </Button>
            </div>
        </div>
    )
}

/**
 * Khối "Bù đơn đang nợ hàng" cho MỘT dòng lô của phiếu nhập (FD-372).
 *
 * Chỉ áp cho dòng LÔ MỚI: lô có sẵn không tra được sản phẩm từ option của
 * picker, mà không biết sản phẩm thì không biết đơn nào đang nợ. Luồng thật của
 * việc bù nợ cũng là đóng gói xong rồi nhập lô MỚI vào kho.
 */
function DebtFillForItem({ idx }: { idx: number }) {
    const form = useFormContext<FormData>()
    const kind = useWatch({ control: form.control, name: `items.${idx}.kind` })
    const productId = useWatch({
        control: form.control,
        name: `items.${idx}.new_lot_product_id`,
    })
    const quantity = useWatch({
        control: form.control,
        name: `items.${idx}.quantity`,
    })
    const value = useWatch({
        control: form.control,
        name: `items.${idx}.debt_allocations`,
    })
    // Phiếu được phép có nhiều dòng lô cùng một sản phẩm; các khối bù phải thấy
    // phần nhau đã hứa, nếu không cả hai cùng "Bù hết" cho một đơn và màn hình
    // báo tổng gấp đôi số thật đi bù.
    const allRows = useWatch({ control: form.control, name: 'items' })
    const usedByOtherRows = useMemo(
        () => sumOtherRowsFill(allRows ?? [], idx),
        [allRows, idx],
    )

    if (kind !== 'new') return null

    return (
        <LotDebtFillSection
            productId={productId || undefined}
            lotQuantity={Number(quantity) || 0}
            value={value ?? {}}
            usedByOtherRows={usedByOtherRows}
            onChange={(next) =>
                form.setValue(`items.${idx}.debt_allocations`, next, {
                    shouldDirty: true,
                })
            }
        />
    )
}

// Bảng vật tư con của MỘT dòng lô sản phẩm (from_packaging, tuỳ chọn).
function MaterialsSubTable({
    idx,
    options,
    onSearchChange,
    clientFilter,
    onLoadMore,
    hasMore,
    loadingMore,
}: {
    idx: number
    options: LotOption[]
    onSearchChange: (s: string) => void
    clientFilter?: boolean
} & MaterialLoadMore) {
    const { control } = useFormContext<FormData>()
    const { fields, append, remove } = useFieldArray({
        control,
        name: `items.${idx}.materials`,
    })

    return (
        <div className="mt-1 rounded-md border border-dashed bg-muted/20 p-2">
            <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">
                    {m.pll_new_materials_title()}
                </span>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                        append({
                            packaging_lot_id: '',
                            quantity: undefined,
                        })
                    }
                >
                    <Plus className="size-4 mr-1" />
                    {m.pll_new_material_add()}
                </Button>
            </div>
            {fields.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                    {m.pll_new_materials_empty()}
                </p>
            ) : (
                fields.map((f, mIdx) => (
                    <MaterialRow
                        key={f.id}
                        idx={idx}
                        mIdx={mIdx}
                        options={options}
                        onSearchChange={onSearchChange}
                        clientFilter={clientFilter}
                        onRemove={() => remove(mIdx)}
                        onLoadMore={onLoadMore}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                    />
                ))
            )}
        </div>
    )
}

type ProductLotStockInFormProps = {
    mode: 'create' | 'edit'
    title: string
    submitLabel: string
    isPending: boolean
    defaultValues: FormData
    onSubmit: (
        req: CreateInNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => Promise<void>
    onCancel: () => void
    /** Lô lấy hương nguồn đã chọn (để sửa hiện đúng dù ngoài top-search). */
    seedSourceOptions?: LotOption[]
    /** Lô vật tư đã chọn (để sửa hiện đúng dù ngoài top-search / note đã cạn). */
    seedMaterialOptions?: LotOption[]
    /** Phiếu lấy hàng lấy hương đã chọn khi tạo — dùng để sửa pre-select lại picker. */
    initialWithdrawalNoteId?: string
    /** Phiếu lấy hàng vật tư đã chọn khi tạo — dùng để sửa pre-select lại picker. */
    initialPackagingWithdrawalNoteId?: string
    // Lô của item hiện có khi sửa. Lô mới do PNK tạo ra đang 'pending' nên bị query
    // (chỉ trả lô active) lọc mất → seed để ô "Chọn lô" hiển thị đúng nhãn.
    seedLotOptions?: ProductLotOption[]
    /** Mở từ một cuốn sổ: lý do phiếu do sổ quyết định, không cho đổi. Server
     *  cũng từ chối gắn phiếu sai lý do về dòng sổ, khoá ở đây để người dùng
     *  khỏi điền xong mới bị chặn. */
    lockReason?: boolean
}

const searchSchema = z.object({
    pxk: z.string().uuid().optional(),
    pxbb: z.string().uuid().optional(),
    // Dòng sổ đóng gói 12,5g đã sinh ra lần tạo phiếu này (#M-16). Có thì form
    // được điền sẵn từ dòng sổ và phiếu sẽ gắn ngược về dòng đó khi lưu.
    packagingLogEntryId: z.string().uuid().optional(),
    // Dòng sổ đóng hộp đã sinh ra lần tạo phiếu này (#M-16). Cùng cơ chế, khác
    // lý do phiếu: túi -> hộp thay vì chè -> túi.
    boxLogEntryId: z.string().uuid().optional(),
    // Dòng sổ ĐÓNG GÓI KHÁC đã sinh ra lần tạo phiếu này (#M-16). Mẻ đó ra nhiều
    // quy cách nên phiếu được điền sẵn NHIỀU dòng lô.
    otherLogEntryId: z.string().uuid().optional(),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/stock-in-notes/new',
)({
    component: NewProductLotInNotePage,
    validateSearch: searchSchema,
})

function NewProductLotInNotePage() {
    const navigate = useNavigate()
    const search = Route.useSearch()
    const create = useCreateProductLotInNote()

    const initialNSX = useMemo(() => today(), [])
    const initialHSD = useMemo(() => addOneYear(today()), [])

    // Mở từ sổ đóng gói: đọc dòng sổ để điền sẵn. Chờ tải xong mới dựng form,
    // nếu không form đã mount với giá trị rỗng và không nhận lại dữ liệu.
    const { data: logEntry, isLoading: logLoading } = usePackagingLogEntry(
        search.packagingLogEntryId,
    )
    const { data: boxLogEntry, isLoading: boxLogLoading } =
        usePackagingBoxLogEntry(search.boxLogEntryId)
    const { data: otherLogEntry, isLoading: otherLogLoading } =
        usePackagingOtherLogEntry(search.otherLogEntryId)

    const defaultValues = useMemo<FormData>(
        () => ({
            reason: 'from_packaging',
            scenting_lot_stock_out_note_id: search.pxk ?? '',
            packaging_lot_stock_out_note_id: search.pxbb ?? '',
            notes: '',
            items: [
                {
                    kind: 'new' as const,
                    new_lot_product_id: '',
                    // Phải là '' (không để undefined): z.string().uuid() nhận
                    // undefined là lỗi *type* fatal → zod v4 abort cả item, bỏ
                    // qua superRefine cấp form → lỗi "lô nguồn/kg" không hiện.
                    // '' fail .uuid() non-fatal nên superRefine vẫn chạy.
                    new_lot_position_id: '',
                    new_lot_manufactured_date: initialNSX,
                    new_lot_expiry_date: initialHSD,
                    quantity: 0,
                    materials: [],
                } as FormData['items'][number],
            ],
        }),
        [search.pxk, search.pxbb, initialNSX, initialHSD],
    )

    // Giá trị điền sẵn từ dòng sổ thay cho mặc định rỗng.
    const prefilled = useMemo<FormData | null>(() => {
        const otherEntry = otherLogEntry?.data
        if (otherEntry) {
            return otherLogEntryToFormValues(otherEntry, {
                expiryDate: initialHSD,
            }) as unknown as FormData
        }
        const boxEntry = boxLogEntry?.data
        if (boxEntry) {
            return boxLogEntryToFormValues(boxEntry, {
                expiryDate: initialHSD,
            }) as unknown as FormData
        }
        const entry = logEntry?.data
        if (!entry) return null
        return logEntryToFormValues(entry, {
            expiryDate: initialHSD,
        }) as unknown as FormData
    }, [logEntry, boxLogEntry, otherLogEntry, initialHSD])

    // Nhãn lô nguồn của dòng sổ: ô chọn lô nguồn tải dần theo trang, lô của
    // sổ có thể chưa nằm trong trang đầu — thiếu seed thì ô hiện placeholder
    // dù giá trị vẫn đúng (M-68).
    const prefillSourceOptions = useMemo<LotOption[]>(() => {
        const scentingSources =
            otherLogEntry?.data?.sources ?? logEntry?.data?.sources
        if (scentingSources) {
            return scentingSources.map((src) =>
                ledgerSourceNoteOption({
                    id: src.source_scenting_lot_id,
                    code: src.source_lot_code,
                    name: src.source_grade_name,
                    available: src.source_available,
                    unit: 'kg',
                }),
            )
        }
        return (boxLogEntry?.data?.sources ?? []).map((src) =>
            ledgerSourceNoteOption({
                id: src.source_product_lot_id,
                code: src.source_lot_code,
                name: src.source_product_name,
                available: src.source_available,
                // Lô thành phẩm: không có đơn vị trên dòng sổ — để rỗng chứ
                // không đoán 'kg'.
                unit: '',
            }),
        )
    }, [logEntry, boxLogEntry, otherLogEntry])

    const handleSubmit = async (
        req: CreateInNoteRequest,
        workflow: ReturnType<typeof useWorkflowTracking>,
    ) => {
        const res = await workflow.run(
            () =>
                create.mutateAsync({
                    ...req,
                    // Gắn phiếu ngược về dòng sổ ngay trong transaction tạo phiếu.
                    log_entry_id: search.packagingLogEntryId,
                    box_log_entry_id: search.boxLogEntryId,
                    other_log_entry_id: search.otherLogEntryId,
                }),
            (result) => result.data?.id,
        )
        const noteId = (res as { data?: { id?: string } }).data?.id
        if (noteId) {
            void navigate({
                to: '/sales-warehouse/stock-in-notes/$id',
                params: { id: noteId },
                replace: true,
            })
        }
    }

    if (
        (search.packagingLogEntryId && logLoading) ||
        (search.boxLogEntryId && boxLogLoading) ||
        (search.otherLogEntryId && otherLogLoading)
    ) {
        return (
            <p className="p-6 text-center text-muted-foreground">
                {m.common_loading()}
            </p>
        )
    }

    return (
        <ProductLotStockInForm
            mode="create"
            title={m.pll_new_pnk_title()}
            submitLabel={m.sl_new_submit_pnk()}
            isPending={create.isPending}
            defaultValues={prefilled ?? defaultValues}
            seedSourceOptions={prefillSourceOptions}
            lockReason={
                !!search.packagingLogEntryId ||
                !!search.boxLogEntryId ||
                !!search.otherLogEntryId
            }
            onSubmit={handleSubmit}
            onCancel={() =>
                void navigate({
                    to: '/sales-warehouse/stock-in-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

export function ProductLotStockInForm({
    mode,
    title,
    submitLabel,
    isPending,
    defaultValues,
    onSubmit,
    onCancel,
    seedLotOptions = [],
    seedSourceOptions = [],
    seedMaterialOptions = [],
    initialWithdrawalNoteId,
    initialPackagingWithdrawalNoteId,
    lockReason = false,
}: ProductLotStockInFormProps) {
    const workflow = useWorkflowTracking('stock_in_create', {
        operation: mode === 'edit' ? 'update' : 'create',
    })
    const isEdit = mode === 'edit'

    // Ô "lô có sẵn": tìm server + cuộn tải thêm (nguồn lô dùng chung). Seed =
    // lô của item hiện có khi sửa — lô mới do PNK tạo đang 'pending' nên danh
    // sách (chỉ lô active) không trả, seed giữ nhãn đúng.
    const lotSource = useProductLotSource({ seed: seedLotOptions })

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues,
    })

    const reason = useWatch({ control: form.control, name: 'reason' })

    const isFromPackaging = reason === 'from_packaging'
    const isFromProductPackaging = reason === 'from_product_packaging'
    // Cả 2 lý do đi chuỗi: khai nguồn tầng dòng + vật tư giống nhau. Khác duy nhất
    // ở picker lô nguồn chính (lô lấy hương vs lô thành phẩm túi lẻ).
    const isChain = isFromPackaging || isFromProductPackaging
    // Dần chè (FD-373): không có lô nguồn nên KHÔNG hiện bảng lô nguồn, nhưng
    // vẫn khai được vật tư đóng gói (mặt hàng tấm vẫn tốn túi/hộp/tem).
    const allowsMaterials = isChain || reason === 'from_tea_sifting'

    // Picker lô thành phẩm nguồn (túi lẻ) cho reason=from_product_packaging: tìm
    // server + cuộn tải thêm, chỉ lô còn tồn + là SP dùng để đóng gói
    // (is_packaging_source). Seed trước để lô đã chọn khi sửa vẫn hiện.
    const productSourceLotSource = useProductLotSource({
        filters: { only_available: '1', is_packaging_source: '1' },
        seed: seedSourceOptions,
        enabled: isFromProductPackaging,
    })

    // Gợi ý theo phiếu lấy hàng kho lấy hương (tuỳ chọn). Search theo tiêu đề,
    // debounce qua API, cuộn tải thêm. Chọn note → picker lô nguồn chỉ hiện lô
    // trong note. BE trừ số dư note khi tạo.
    const [withdrawalNoteId, setWithdrawalNoteId] = useState(
        initialWithdrawalNoteId ?? '',
    )
    const [withdrawalSearch, setWithdrawalSearch] = useState('')
    const debouncedWithdrawalSearch = useDebounced(withdrawalSearch, 300)
    const withdrawalNotesQuery = useScentingWithdrawalNotesInfinite(
        {
            per_page: 20,
            status: 'open',
            search: debouncedWithdrawalSearch || undefined,
        },
        { enabled: isFromPackaging },
    )
    const withdrawalNoteOptions = useMemo(
        () =>
            (withdrawalNotesQuery.data?.pages ?? [])
                .flatMap((p) => p.data ?? [])
                .map((n) => ({
                    value: n.id,
                    label: `${n.notes ?? n.code} · ${n.code}`,
                })),
        [withdrawalNotesQuery.data],
    )
    const { data: pickedNoteData } = useScentingWithdrawalNote(
        withdrawalNoteId || undefined,
    )
    const noteSourceOptions = useMemo<LotOption[]>(
        () =>
            (pickedNoteData?.data?.items ?? [])
                .filter((it) => it.quantity > 0)
                .map((it) => ({
                    value: it.scenting_lot_id,
                    label: `${it.scenting_lot_code ?? it.scenting_lot_id} — ${it.grade_name ?? ''} (${m.scl_new_source_remaining({ qty: formatQty(it.quantity) })})`,
                    available: it.quantity,
                    unit: 'kg',
                })),
        [pickedNoteData],
    )

    const usingNote = !!withdrawalNoteId
    // Picker lô lấy hương nguồn (per-line): không chọn phiếu lấy hàng thì tìm
    // server + cuộn tải thêm, chỉ lô còn tồn (nguồn lô dùng chung).
    const sourceLotSource = useScentingLotSource({
        filters: { only_available: '1' },
        seed: seedSourceOptions,
        enabled: isFromPackaging && !usingNote,
    })
    const sourceLotOptions = useMemo<LotOption[]>(() => {
        if (!usingNote) return sourceLotSource.options
        const map = new Map<string, LotOption>()
        for (const opt of seedSourceOptions) map.set(opt.value, opt)
        for (const opt of noteSourceOptions) map.set(opt.value, opt) // số dư mới
        return Array.from(map.values())
    }, [
        usingNote,
        noteSourceOptions,
        sourceLotSource.options,
        seedSourceOptions,
    ])

    // ---- Vật tư (tuỳ chọn): picker lô vật tư + phiếu lấy hàng kho vật tư ----

    // ---- Tự điền vật tư theo ĐỊNH MỨC (#M-16) ----
    //
    // Trước đây việc này làm lúc tạo ĐƠN HÀNG và ở máy POS. Nhưng vật tư chỉ
    // thật sự rời kho khi lập phiếu nhập lô sản phẩm, nên gợi ý ở đây mới đúng
    // chỗ — và người lập phiếu nhìn thấy luôn lô nào bị trừ. Phiếu tạo từ sổ
    // xưởng cũng đi qua chính form này nên được điền y hệt.
    const checkBom = useCheckPackagingAvailability()
    // Danh sách lô để RẢI, tách khỏi `materialLotsData` (vốn phục vụ ô tìm kiếm
    // nên chỉ 50 dòng theo từ khoá): rải mà thiếu lô thì báo thiếu oan.
    const { data: allMaterialLotsData } = usePackagingLots(
        { per_page: 500, only_available: '1' },
        { enabled: allowsMaterials },
    )
    const watchedItems = useWatch({ control: form.control, name: 'items' })
    // Khoá so sánh: chỉ các dòng LÔ MỚI đã có sản phẩm và số lượng. Dòng lô có
    // sẵn không tra được sản phẩm nên không có định mức nào để điền.
    const bomKey = useMemo(() => {
        if (!allowsMaterials) return ''
        return (watchedItems ?? [])
            .map((it, i) =>
                it?.kind === 'new' &&
                it?.new_lot_product_id &&
                Number(it?.quantity) > 0
                    ? `${i}::${it.new_lot_product_id}::${Number(it.quantity)}`
                    : '',
            )
            .filter(Boolean)
            .join('|')
    }, [watchedItems, allowsMaterials])

    const bomDoneRef = useRef('')
    // Số thứ tự lượt tra: người lập phiếu đổi sản phẩm liên tục, mỗi lần đổi là
    // một lượt mới. Kết quả của lượt CŨ về sau sẽ điền vật tư của sản phẩm đã bị
    // thay — phải bỏ đi.
    const bomRunRef = useRef(0)
    // Bản vật tư MÁY đã điền cho từng dòng (chuỗi hóa để so bằng). Dùng để phân
    // biệt "người lập phiếu đã sửa" với "vẫn đúng bản máy điền": đổi sản phẩm
    // thì bản máy điền cũ phải được THAY, còn tay người thì không được đụng.
    const bomFilledRef = useRef<Record<number, string>>({})
    useEffect(() => {
        if (!bomKey || bomKey === bomDoneRef.current) return
        // Chưa có danh sách lô thì chưa rải được. KHÔNG đánh dấu đã xong ở đây:
        // lô về chậm hơn kết quả định mức là chuyện bình thường, đánh dấu sớm
        // thì dòng đó vĩnh viễn không bao giờ được điền.
        const lotRows = allMaterialLotsData?.data ?? []
        if (lotRows.length === 0) return

        const run = ++bomRunRef.current
        const timer = setTimeout(() => {
            const needs = bomKey.split('|').map((row) => {
                const [line, productId, qty] = row.split('::')
                return {
                    line_key: line,
                    product_id: productId,
                    quantity: Number(qty),
                }
            })
            void checkBom
                .mutateAsync(needs)
                .then((res) => {
                    // Đã có lượt tra mới hơn (người dùng vừa đổi sản phẩm hoặc
                    // số lượng) → kết quả này đã lỗi thời, bỏ.
                    if (run !== bomRunRef.current) return
                    bomDoneRef.current = bomKey

                    const lots = lotRows.map((l) => ({
                        id: l.id,
                        packaging_material_id: l.packaging_material_id,
                        quantity_available: l.quantity_available,
                        created_at: l.created_at,
                    }))
                    const byLine = new Map<
                        string,
                        Array<{ material_id: string; quantity: number }>
                    >()
                    for (const it of res.data?.items ?? []) {
                        const list = byLine.get(it.line_key) ?? []
                        list.push({
                            material_id: it.material_id,
                            quantity: it.suggested_qty,
                        })
                        byLine.set(it.line_key, list)
                    }

                    const short: string[] = []
                    // Duyệt theo các dòng ĐÃ HỎI, không theo kết quả trả về:
                    // sản phẩm mới không có định mức thì kết quả rỗng, mà rỗng
                    // nghĩa là phải DỌN vật tư máy điền cho sản phẩm cũ — bỏ
                    // qua là phiếu trừ nhầm kho.
                    for (const need of needs) {
                        const line = need.line_key
                        const list = byLine.get(line) ?? []
                        const idx = Number(line)
                        // KHÔNG đè lên vật tư người lập phiếu đã tự chọn hoặc
                        // sửa: định mức chỉ là gợi ý, quyết định cuối là của họ.
                        // Nhưng bản do CHÍNH MÁY điền lần trước thì phải thay —
                        // đổi sản phẩm mà giữ nguyên vật tư của sản phẩm cũ là
                        // phiếu trừ nhầm kho.
                        const current =
                            form.getValues(`items.${idx}.materials`) ?? []
                        const touchedByUser =
                            current.length > 0 &&
                            JSON.stringify(current) !==
                                bomFilledRef.current[idx]
                        if (touchedByUser) continue

                        const { rows, missingByMaterial } = autofillMaterials(
                            list,
                            lots,
                        )
                        form.setValue(`items.${idx}.materials`, rows, {
                            shouldValidate: false,
                        })
                        bomFilledRef.current[idx] = JSON.stringify(rows)
                        // Kho không đủ vật tư thì phải NÓI RA. Điền thiếu trong
                        // im lặng là người lập phiếu tưởng đã đủ, đến lúc duyệt
                        // mới vỡ.
                        for (const mid of Object.keys(missingByMaterial)) {
                            const name =
                                (res.data?.items ?? []).find(
                                    (x) => x.material_id === mid,
                                )?.material_name ?? mid
                            short.push(
                                `${name}: ${formatQty(missingByMaterial[mid])}`,
                            )
                        }
                    }
                    if (short.length > 0) {
                        toast.warning(
                            m.pll_new_material_autofill_short({
                                list: short.join(', '),
                            }),
                        )
                    }
                })
                .catch(() => {
                    // Tra định mức hỏng thì để trống cho người lập phiếu tự
                    // khai — không được chặn việc lập phiếu.
                })
        }, 400)
        return () => clearTimeout(timer)
        // Cố tình chỉ theo `bomKey` và danh sách lô: `form`/`checkBom` đổi
        // tham chiếu mỗi lần render, đưa vào deps là bắn hàng loạt request.
    }, [bomKey, allMaterialLotsData])

    // Gợi ý theo phiếu lấy hàng kho vật tư (tuỳ chọn, tách hẳn phiếu lấy hương).
    const [packagingWithdrawalNoteId, setPackagingWithdrawalNoteId] = useState(
        initialPackagingWithdrawalNoteId ?? '',
    )
    const [pkgWithdrawalSearch, setPkgWithdrawalSearch] = useState('')
    const debouncedPkgWithdrawalSearch = useDebounced(pkgWithdrawalSearch, 300)
    const pkgWithdrawalNotesQuery = usePackagingWithdrawalNotesInfinite(
        {
            per_page: 20,
            status: 'open',
            search: debouncedPkgWithdrawalSearch || undefined,
        },
        { enabled: isFromPackaging },
    )
    const pkgWithdrawalNoteOptions = useMemo(
        () =>
            (pkgWithdrawalNotesQuery.data?.pages ?? [])
                .flatMap((p) => p.data ?? [])
                .map((n) => ({
                    value: n.id,
                    label: `${n.notes ?? n.code} · ${n.code}`,
                })),
        [pkgWithdrawalNotesQuery.data],
    )
    const { data: pickedPkgNoteData } = usePackagingWithdrawalNote(
        packagingWithdrawalNoteId || undefined,
    )
    const noteMaterialOptions = useMemo<LotOption[]>(
        () =>
            (pickedPkgNoteData?.data?.items ?? [])
                .filter((it) => it.quantity > 0)
                .map((it) => ({
                    value: it.packaging_lot_id,
                    label: `${it.packaging_lot_code ?? it.packaging_lot_id} — ${it.material_name ?? ''} (${m.scl_new_source_remaining({ qty: formatQty(it.quantity) })})`,
                    available: it.quantity,
                    // Vật tư: phiếu lấy hàng không kèm đơn vị → để rỗng thay vì
                    // 'kg' sai. (BE chưa trả material_unit cho item phiếu này.)
                    unit: '',
                })),
        [pickedPkgNoteData],
    )

    const usingPkgNote = !!packagingWithdrawalNoteId
    // Tìm lô vật tư phía server + cuộn tải thêm (khi KHÔNG chọn phiếu lấy hàng
    // vật tư). Seed = lô vật tư đã chọn khi sửa.
    const materialLotSource = usePackagingLotSource({
        filters: { only_available: '1' },
        seed: seedMaterialOptions,
        enabled: allowsMaterials && !usingPkgNote,
    })
    const materialLotOptions = useMemo<LotOption[]>(() => {
        if (!usingPkgNote) return materialLotSource.options
        const map = new Map<string, LotOption>()
        for (const opt of seedMaterialOptions) map.set(opt.value, opt)
        for (const opt of noteMaterialOptions) map.set(opt.value, opt)
        return Array.from(map.values())
    }, [
        usingPkgNote,
        noteMaterialOptions,
        materialLotSource.options,
        seedMaterialOptions,
    ])

    // Đổi lý do sang "điều chỉnh" thì 2 link nguồn không còn nghĩa — xoá khỏi
    // form state. Khoá reason ở edit nên effect này chỉ chạy thực ở màn tạo.
    useEffect(() => {
        if (isFromPackaging) return
        form.setValue('scenting_lot_stock_out_note_id', '')
        form.setValue('packaging_lot_stock_out_note_id', '')
    }, [isFromPackaging, form])

    const [scannedLots, setScannedLots] = useState<
        Map<
            string,
            { value: string; label: string; available?: number; unit?: string }
        >
    >(new Map())
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await productLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, productLotToOption(l))
                return next
            })
        } catch {
            // Bỏ qua — row vẫn giữ đúng ID trong form state.
        }
    }

    // Lỗi thiếu vật tư đóng gói do BE trả về. Toast biến mất nhanh mà danh sách
    // vật tư thiếu cần đọc kỹ → giữ luôn trên form cho tới lần submit sau.
    const [submitError, setSubmitError] = useState<string | null>(null)

    useEffect(() => {
        const subscription = form.watch((_values, { name }) => {
            if (!name) return
            workflow.interact(name.startsWith('items') ? 'items' : 'details')
            if (name.endsWith('product_lot_id') || name === 'reason')
                workflow.step('selection_changed')
        })
        return () => subscription.unsubscribe()
    }, [form, workflow])

    const submit = form.handleSubmit(
        async (data) => {
            setSubmitError(null)
            // Cả 2 lý do đi chuỗi gửi sources[]/materials[] giống hệt nhau. Với
            // from_product_packaging, source_lot_id mang id lô THÀNH PHẨM (túi lẻ).
            const chain =
                data.reason === 'from_packaging' ||
                data.reason === 'from_product_packaging'
            // Dần chè: không gửi sources[] nhưng vẫn gửi materials[].
            const sendsMaterials = chain || data.reason === 'from_tea_sifting'
            // Phiếu lấy hàng (withdrawal) chỉ dùng cho from_packaging.
            const usesWithdrawal = data.reason === 'from_packaging'
            const items: CreateInNoteItem[] = data.items.map((it) => {
                const src = {
                    ...(chain
                        ? {
                              sources: (it.sources ?? [])
                                  .filter((s) => s.source_lot_id)
                                  .map((s) => ({
                                      source_lot_id: s.source_lot_id as string,
                                      source_quantity: Number(
                                          s.source_quantity ?? 0,
                                      ),
                                  })),
                          }
                        : {}),
                    // Vật tư tuỳ chọn — chỉ gửi dòng đã chọn lô + kg hợp lệ.
                    ...(sendsMaterials
                        ? {
                              materials: (it.materials ?? [])
                                  .filter(
                                      (mrow) =>
                                          mrow.packaging_lot_id &&
                                          mrow.quantity &&
                                          mrow.quantity > 0,
                                  )
                                  .map((mrow) => ({
                                      packaging_lot_id:
                                          mrow.packaging_lot_id as string,
                                      quantity: Number(mrow.quantity),
                                  })),
                          }
                        : {}),
                }
                // Bù nợ lô: bỏ dòng 0, đổi record → mảng đúng shape API.
                const debtAllocations = Object.entries(
                    it.debt_allocations ?? {},
                )
                    .filter(([, qty]) => Number(qty) > 0)
                    .map(([lot_debt_id, qty]) => ({
                        lot_debt_id,
                        quantity: Number(qty),
                    }))
                const debt =
                    debtAllocations.length > 0
                        ? { debt_allocations: debtAllocations }
                        : {}
                return it.kind === 'new'
                    ? ({
                          new_lot_product_id: it.new_lot_product_id,
                          new_lot_position_id: it.new_lot_position_id,
                          new_lot_code: it.new_lot_code || undefined,
                          new_lot_manufactured_date:
                              it.new_lot_manufactured_date || undefined,
                          new_lot_expiry_date:
                              it.new_lot_expiry_date || undefined,
                          new_lot_discount_percent: normalizeLotDiscount(
                              it.new_lot_discount_percent,
                          ),
                          log_output_id: it.log_output_id || undefined,
                          quantity: Number(it.quantity),
                          ...src,
                          ...debt,
                      } satisfies CreateInNoteItem)
                    : ({
                          product_lot_id: it.product_lot_id,
                          quantity: Number(it.quantity),
                          ...src,
                      } satisfies CreateInNoteItem)
            })

            try {
                await onSubmit(
                    {
                        reason: data.reason,
                        notes: data.notes || undefined,
                        // Cả PXK chè lẫn PXK vật tư do hệ thống tự sinh lúc duyệt.
                        source_withdrawal_note_id:
                            usesWithdrawal && withdrawalNoteId
                                ? withdrawalNoteId
                                : undefined,
                        packaging_withdrawal_note_id:
                            usesWithdrawal && packagingWithdrawalNoteId
                                ? packagingWithdrawalNoteId
                                : undefined,
                        items,
                    },
                    workflow,
                )
            } catch (err) {
                const msg =
                    err instanceof Error ? err.message : m.sl_new_error_create()
                setSubmitError(msg)
                toast.error(msg)
            }
        },
        () => workflow.step('validation_failed'),
    )

    return (
        <div className="space-y-4 page-pad pb-24 md:pb-6">
            <div className="flex items-center gap-3">
                <PageBackButton onBack={onCancel} />
                <h1 className="text-xl font-semibold">{title}</h1>
            </div>

            <FormProvider {...form}>
                <form
                    onSubmit={submit}
                    onFocusCapture={() => workflow.interact('details')}
                    className="space-y-3 md:space-y-6 max-w-5xl"
                >
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4 [&>*]:min-w-0">
                        <div>
                            <Label>{m.sl_new_reason_in()}</Label>
                            <Select
                                value={reason}
                                disabled={isEdit || lockReason}
                                onValueChange={(v) =>
                                    form.setValue(
                                        'reason',
                                        v as FormData['reason'],
                                    )
                                }
                            >
                                <SelectTrigger className="w-full mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="from_packaging">
                                        {m.pll_in_reason_from_packaging()}
                                    </SelectItem>
                                    <SelectItem value="from_product_packaging">
                                        {m.pll_in_reason_from_product_packaging()}
                                    </SelectItem>
                                    <SelectItem value="adjustment">
                                        {m.pll_in_reason_adjustment()}
                                    </SelectItem>
                                    <SelectItem value="from_manufacturing_partner">
                                        {m.pll_in_reason_from_manufacturing_partner()}
                                    </SelectItem>
                                    <SelectItem value="from_tea_sifting">
                                        {m.pll_in_reason_from_tea_sifting()}
                                    </SelectItem>
                                    <SelectItem value="from_agent_return">
                                        {m.pll_in_reason_from_agent_return()}
                                    </SelectItem>
                                    {/* Chỉ hiện khi phiếu đang sửa mang lý do tạo tự động,
                                        để Select disabled vẫn render đúng nhãn. */}
                                    {reason === 'from_customer_return' && (
                                        <SelectItem value="from_customer_return">
                                            {m.pll_in_reason_from_customer_return()}
                                        </SelectItem>
                                    )}
                                    {reason === 'from_order_cancel' && (
                                        <SelectItem value="from_order_cancel">
                                            {m.pll_in_reason_from_order_cancel()}
                                        </SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{m.sl_new_notes()}</Label>
                            <textarea
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                                rows={2}
                                className="mt-1 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm"
                            />
                        </div>

                        {WITHDRAWAL_UI_ENABLED && isFromPackaging && (
                            <>
                                <div className="md:col-span-2">
                                    <Label className="mb-1 block">
                                        {m.pll_new_withdrawal_note_label()}
                                    </Label>
                                    <Combobox
                                        options={[
                                            {
                                                value: '',
                                                label: m.pll_new_withdrawal_note_none(),
                                            },
                                            ...withdrawalNoteOptions,
                                        ]}
                                        value={withdrawalNoteId}
                                        onChange={(v) => {
                                            setWithdrawalNoteId(v)
                                            // Đổi phiếu → xoá lô nguồn đã chọn để
                                            // buộc chọn lại theo phiếu mới.
                                            const its =
                                                form.getValues('items') ?? []
                                            its.forEach((_, i) => {
                                                form.setValue(
                                                    `items.${i}.sources`,
                                                    [],
                                                )
                                            })
                                        }}
                                        onSearchChange={setWithdrawalSearch}
                                        shouldFilter={false}
                                        selectedOption={
                                            withdrawalNoteId &&
                                            pickedNoteData?.data
                                                ? {
                                                      value: pickedNoteData.data
                                                          .id,
                                                      label: `${pickedNoteData.data.notes ?? pickedNoteData.data.code} · ${pickedNoteData.data.code}`,
                                                  }
                                                : undefined
                                        }
                                        onOpenChange={(open) => {
                                            if (!open) setWithdrawalSearch('')
                                        }}
                                        onLoadMore={() =>
                                            void withdrawalNotesQuery.fetchNextPage()
                                        }
                                        hasMore={
                                            withdrawalNotesQuery.hasNextPage
                                        }
                                        placeholder={m.pll_new_withdrawal_note_placeholder()}
                                    />
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        {m.pll_new_source_hint()}
                                    </p>
                                </div>

                                <div className="md:col-span-2">
                                    <Label className="mb-1 block">
                                        {m.pll_new_pkg_withdrawal_note_label()}
                                    </Label>
                                    <Combobox
                                        options={[
                                            {
                                                value: '',
                                                label: m.pll_new_withdrawal_note_none(),
                                            },
                                            ...pkgWithdrawalNoteOptions,
                                        ]}
                                        value={packagingWithdrawalNoteId}
                                        onChange={(v) => {
                                            setPackagingWithdrawalNoteId(v)
                                            // Đổi phiếu → xoá vật tư đã chọn để
                                            // buộc chọn lại theo phiếu mới.
                                            const its =
                                                form.getValues('items') ?? []
                                            its.forEach((_, i) => {
                                                form.setValue(
                                                    `items.${i}.materials`,
                                                    [],
                                                )
                                            })
                                        }}
                                        onSearchChange={setPkgWithdrawalSearch}
                                        shouldFilter={false}
                                        selectedOption={
                                            packagingWithdrawalNoteId &&
                                            pickedPkgNoteData?.data
                                                ? {
                                                      value: pickedPkgNoteData
                                                          .data.id,
                                                      label: `${pickedPkgNoteData.data.notes ?? pickedPkgNoteData.data.code} · ${pickedPkgNoteData.data.code}`,
                                                  }
                                                : undefined
                                        }
                                        onOpenChange={(open) => {
                                            if (!open)
                                                setPkgWithdrawalSearch('')
                                        }}
                                        onLoadMore={() =>
                                            void pkgWithdrawalNotesQuery.fetchNextPage()
                                        }
                                        hasMore={
                                            pkgWithdrawalNotesQuery.hasNextPage
                                        }
                                        placeholder={m.pll_new_pkg_withdrawal_note_placeholder()}
                                    />
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        {m.pll_new_material_hint()}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>

                    <ProductLotItemsField
                        mode="in"
                        lotSource={lotSource}
                        scannedLots={Array.from(scannedLots.values())}
                        renderExtraPerItem={(idx) => (
                            <>
                                {isChain && (
                                    <LotSourcesSubTable
                                        idx={idx}
                                        options={
                                            isFromProductPackaging
                                                ? productSourceLotSource.options
                                                : sourceLotOptions
                                        }
                                        onSearchChange={
                                            isFromProductPackaging
                                                ? productSourceLotSource.onSearchChange
                                                : sourceLotSource.onSearchChange
                                        }
                                        clientFilter={
                                            isFromProductPackaging
                                                ? false
                                                : usingNote
                                        }
                                        {...(isFromProductPackaging
                                            ? {
                                                  onLoadMore:
                                                      productSourceLotSource.onLoadMore,
                                                  hasMore:
                                                      productSourceLotSource.hasMore,
                                                  loadingMore:
                                                      productSourceLotSource.loadingMore,
                                              }
                                            : usingNote
                                              ? {}
                                              : {
                                                    onLoadMore:
                                                        sourceLotSource.onLoadMore,
                                                    hasMore:
                                                        sourceLotSource.hasMore,
                                                    loadingMore:
                                                        sourceLotSource.loadingMore,
                                                })}
                                        labels={
                                            isFromProductPackaging
                                                ? {
                                                      title: m.pll_new_product_sources_title(),
                                                      empty: m.pll_new_sources_empty(),
                                                      add: m.pll_new_source_add(),
                                                      remove: m.pll_new_source_remove(),
                                                      lotPlaceholder:
                                                          m.pll_new_product_source_lot_placeholder(),
                                                      qtyLabel:
                                                          m.pll_new_source_qty_label(),
                                                  }
                                                : {
                                                      title: m.pll_new_sources_title(),
                                                      empty: m.pll_new_sources_empty(),
                                                      add: m.pll_new_source_add(),
                                                      remove: m.pll_new_source_remove(),
                                                      lotPlaceholder:
                                                          m.pll_new_source_lot_placeholder(),
                                                      qtyLabel:
                                                          m.pll_new_source_qty_label(),
                                                  }
                                        }
                                    />
                                )}
                                {allowsMaterials && (
                                    <MaterialsSubTable
                                        idx={idx}
                                        options={materialLotOptions}
                                        onSearchChange={
                                            materialLotSource.onSearchChange
                                        }
                                        clientFilter={usingPkgNote}
                                        {...(usingPkgNote
                                            ? {}
                                            : {
                                                  onLoadMore:
                                                      materialLotSource.onLoadMore,
                                                  hasMore:
                                                      materialLotSource.hasMore,
                                                  loadingMore:
                                                      materialLotSource.loadingMore,
                                              })}
                                    />
                                )}
                                <DebtFillForItem idx={idx} />
                            </>
                        )}
                        headerAction={
                            <ScanLotCodeButton
                                parseLotId={makeLotIdParser(
                                    'sales-warehouse/lots',
                                )}
                                onScanned={(lotId) => {
                                    const current =
                                        form.getValues('items') ?? []
                                    form.setValue('items', [
                                        ...current,
                                        {
                                            kind: 'existing' as const,
                                            product_lot_id: lotId,
                                            quantity: 0,
                                        },
                                    ] as FormData['items'])
                                    void fetchAndCacheLot(lotId)
                                }}
                            />
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    {submitError && (
                        <div
                            role="alert"
                            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive whitespace-pre-line"
                        >
                            {submitError}
                        </div>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {isPending ? m.sl_new_submitting() : submitLabel}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}

// today trả local-date `YYYY-MM-DD` (không dùng toISOString — nó lệch sang UTC).
function today(): string {
    const d = new Date()
    const y = d.getFullYear()
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${mo}-${dd}`
}

// addOneYear trả YYYY-MM-DD 1 năm sau `iso`. Ngày 29/2 → 28/2 năm sau.
function addOneYear(iso: string): string {
    if (!iso) return ''
    const [ys, ms, ds] = iso.split('-')
    const y = Number(ys)
    const mo = Number(ms)
    const d = Number(ds)
    if (!y || !mo || !d) return ''
    const dt = new Date(y + 1, mo - 1, d)
    if (dt.getMonth() !== mo - 1) dt.setDate(0)
    const ny = dt.getFullYear()
    const nm = String(dt.getMonth() + 1).padStart(2, '0')
    const nd = String(dt.getDate()).padStart(2, '0')
    return `${ny}-${nm}-${nd}`
}
